import { useEffect, useState, useCallback } from 'react'
import {
  Home,
  RefreshCw,
  Loader2,
  AlertCircle,
  Clock,
} from 'lucide-react'
import {
  getInfrastructureStatus,
  getAIMetrics,
  formatUptime,
  getDashboard,
} from '@/api/dashboard'
import {
  InfrastructurePanel,
  AIMetricsPanel,
  ActivityFeed,
} from '@/components/dashboard'
import { useActivityPolling } from '@/hooks/useActivityStream'
import type { InfrastructureStatus, AIMetrics } from '@/types/dashboard'

export default function Dashboard() {
  const [infrastructure, setInfrastructure] = useState<InfrastructureStatus | null>(null)
  const [aiMetrics, setAIMetrics] = useState<AIMetrics | null>(null)
  const [uptime, setUptime] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Activity feed with polling (more reliable than SSE)
  const {
    events: activityEvents,
    connected: activityConnected,
    connect: connectActivity,
  } = useActivityPolling(10000) // Poll every 10 seconds

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch in parallel
      const [infraData, metricsData, dashboardData] = await Promise.all([
        getInfrastructureStatus(),
        getAIMetrics(),
        getDashboard(),
      ])

      setInfrastructure(infraData)
      setAIMetrics(metricsData)
      setUptime(dashboardData.stats.uptime_seconds)
      setLastRefresh(new Date())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAllData()
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000)
    return () => clearInterval(interval)
  }, [fetchAllData])

  if (loading && !infrastructure) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
          <div className="absolute inset-0 w-16 h-16 bg-accent/20 rounded-2xl animate-ping" />
        </div>
        <p className="text-text-muted">Loading command center...</p>
      </div>
    )
  }

  if (error && !infrastructure) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-4">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <p className="text-text-muted text-center">{error}</p>
        <button onClick={fetchAllData} className="btn-primary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 sm:p-6 pb-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
                <Home className="w-5 h-5 text-accent" />
              </div>
              Command Center
            </h1>
            <p className="text-text-muted mt-1 text-sm flex items-center gap-4">
              <span>Monitor your smart home infrastructure</span>
              <span className="flex items-center gap-1 text-xs font-mono">
                <Clock className="w-3 h-3" />
                {formatUptime(uptime)}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted hidden sm:block">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="p-3 rounded-xl hover:bg-surface-hover border border-surface-border transition-all hover:scale-105 active:scale-95"
              title="Refresh"
            >
              <RefreshCw
                className={`w-5 h-5 text-text-muted ${loading ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 pt-4 overflow-hidden">
        <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Left Sidebar - Infrastructure */}
          <div className="lg:col-span-3 h-full overflow-hidden">
            <InfrastructurePanel data={infrastructure} loading={loading} />
          </div>

          {/* Main Area - AI Metrics + Activity */}
          <div className="lg:col-span-9 flex flex-col gap-4 sm:gap-6 overflow-hidden">
            {/* AI Metrics Grid */}
            <div className="flex-shrink-0">
              <AIMetricsPanel data={aiMetrics} loading={loading} />
            </div>

            {/* Activity Feed */}
            <div className="flex-1 min-h-0 relative">
              <ActivityFeed
                events={activityEvents}
                loading={loading}
                connected={activityConnected}
                onRefresh={() => {
                  connectActivity()
                  fetchAllData()
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
